import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  query, orderBy, onSnapshot, serverTimestamp, getDoc, setDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage, deleteImage } from '../cloudinary';

const BlogContext = createContext(null);
const POSTS_COL = 'posts';
const SETTINGS_DOC = 'siteSettings';
const SETTINGS_COL = 'settings';
const SUBSCRIBERS_COL = 'subscribers';

const DEFAULT_SETTINGS = {
  socialLinks: { instagram: '', linkedin: '', twitter: '', email: '' },
  homeContent: '',
  philosophyContent: '',
  aboutContent: '',
  brandLogo: '',
  brandLogoPublicId: '',
};

export function BlogProvider({ children }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [subscribers, setSubscribers] = useState([]);

  // Posts listener
  useEffect(() => {
    const q = query(collection(db, POSTS_COL), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => {
        const raw = d.data();
        return {
          id: d.id,
          ...raw,
          date: raw.createdAt?.toDate?.().toISOString().slice(0, 10) || raw.date || '',
          comments: raw.comments || [],
        };
      });
      setPosts(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Subscribers listener
  useEffect(() => {
    const q = query(collection(db, SUBSCRIBERS_COL), orderBy('subscribedAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setSubscribers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  // Site settings listener
  useEffect(() => {
    const ref = doc(db, SETTINGS_COL, SETTINGS_DOC);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setSiteSettings({ ...DEFAULT_SETTINGS, ...snap.data() });
      }
      setSettingsLoading(false);
    });
    return unsub;
  }, []);

  const updateSiteSettings = useCallback(async (updates) => {
    const ref = doc(db, SETTINGS_COL, SETTINGS_DOC);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await updateDoc(ref, updates);
    } else {
      await setDoc(ref, { ...DEFAULT_SETTINGS, ...updates });
    }
  }, []);

  const addPost = useCallback(async (post, imageFile) => {
    let image = post.image || '';
    let imagePublicId = '';
    if (imageFile) {
      const result = await uploadImage(imageFile);
      image = result.url;
      imagePublicId = result.publicId;
    }
    await addDoc(collection(db, POSTS_COL), {
      ...post, image, imagePublicId, comments: [], createdAt: serverTimestamp(),
    });
  }, []);

  const updatePost = useCallback(async (id, updates, newImageFile) => {
    const docRef = doc(db, POSTS_COL, id);
    const existing = posts.find((p) => p.id === id);
    if (newImageFile) {
      if (existing?.imagePublicId) await deleteImage(existing.imagePublicId);
      const result = await uploadImage(newImageFile);
      updates.image = result.url;
      updates.imagePublicId = result.publicId;
    }
    await updateDoc(docRef, updates);
  }, [posts]);

  const deletePost = useCallback(async (id) => {
    const existing = posts.find((p) => p.id === id);
    if (existing?.imagePublicId) await deleteImage(existing.imagePublicId);
    await deleteDoc(doc(db, POSTS_COL, id));
  }, [posts]);

  const removePostImage = useCallback(async (id) => {
    const existing = posts.find((p) => p.id === id);
    if (existing?.imagePublicId) await deleteImage(existing.imagePublicId);
    await updateDoc(doc(db, POSTS_COL, id), { image: '', imagePublicId: '' });
  }, [posts]);

  const addSubscriber = useCallback(async (email) => {
    // Check for duplicate
    const existing = subscribers.find((s) => s.email === email);
    if (existing) return;
    await addDoc(collection(db, SUBSCRIBERS_COL), {
      email,
      subscribedAt: serverTimestamp(),
    });
  }, [subscribers]);

  const addComment = useCallback(async (postId, comment) => {
    const existing = posts.find((p) => p.id === postId);
    if (!existing) return;
    const updated = [...existing.comments, { ...comment, id: String(Date.now()) }];
    await updateDoc(doc(db, POSTS_COL, postId), { comments: updated });
  }, [posts]);

  const deleteComment = useCallback(async (postId, commentId) => {
    const existing = posts.find((p) => p.id === postId);
    if (!existing) return;
    const updated = existing.comments.filter((c) => c.id !== commentId);
    await updateDoc(doc(db, POSTS_COL, postId), { comments: updated });
  }, [posts]);

  return (
    <BlogContext.Provider value={{
      posts, loading, addPost, updatePost, deletePost, removePostImage, addComment, deleteComment,
      siteSettings, settingsLoading, updateSiteSettings,
      subscribers, addSubscriber,
    }}>
      {children}
    </BlogContext.Provider>
  );
}

export const useBlog = () => useContext(BlogContext);